// Pantry-aware "Suggested for you" recipe section.
//
// Calls recipes.suggestions on mount (and when refreshToken changes),
// then renders a compact section above the saved-recipes grid. Renders
// null when there are no suggestions so it never takes up visual space
// in an empty state.
//
// Each row mirrors the RecipeListRow structure from recipes.tsx:
//   - thumbnail (image or hue-gradient placeholder)
//   - recipe name
//   - reason line  (top expiring ingredient + urgency phrase)
//   - coverage line (have N/M + "need …" + "Add to list" button)
//
// "Add to list" uses the same last-used localStorage key that ListPicker
// and recipe-detail.tsx use, so the household's normal default list is
// always targeted without a picker modal — keeping the interaction quick.

import { useEffect, useState } from "preact/hooks";
import { api, type RecipeSuggestion } from "../../lib/rpc";
import { hueGrad } from "../../icons";
import { hueFromId } from "../../lib/use-recipes";
import type { NotifyFn } from "../../components/toast";

const LAST_LIST_KEY = "hub.recipes.addToList.lastId";

interface RecipeSuggestionsProps {
  notify: NotifyFn;
  onOpenRecipe: (id: string) => void;
  /** Increment to trigger a re-fetch (e.g. after pantry changes). */
  refreshToken?: number | string;
}

function expiryPhrase(days: number | null): string {
  if (days === null) return "expiring soon";
  if (days < 0) return "expired";
  if (days === 0) return "expires today";
  if (days === 1) return "expires tomorrow";
  return `expires in ${days} days`;
}

export function RecipeSuggestions({ notify, onOpenRecipe, refreshToken }: RecipeSuggestionsProps) {
  const [suggestions, setSuggestions] = useState<RecipeSuggestion[]>([]);
  const [adding, setAdding] = useState<number | null>(null); // recipe id being added

  useEffect(() => {
    let cancelled = false;
    api.recipesSuggestions(10)
      .then((list) => { if (!cancelled) setSuggestions(list); })
      .catch(() => { /* silent — suggestions are best-effort */ });
    return () => { cancelled = true; };
  }, [refreshToken]);

  if (suggestions.length === 0) return null;

  const handleAddToList = async (suggestion: RecipeSuggestion) => {
    const missing = suggestion.missing;
    if (missing.length === 0) return;

    // Resolve the target list: last-used from localStorage, or first
    // available list. Matches exactly how ListPicker and recipe-detail
    // pick the default so the user's established default is respected.
    let listId: number | null = null;
    let listName = "your list";
    try {
      const lists = await api.shoppingListLists();
      const active = lists.filter((l) => !l.archived);
      if (active.length === 0) {
        notify("No active shopping lists — create one first.");
        return;
      }
      try {
        const saved = localStorage.getItem(LAST_LIST_KEY);
        const id = saved ? Number(saved) : NaN;
        if (Number.isFinite(id) && active.some((l) => l.id === id)) {
          listId = id;
          listName = active.find((l) => l.id === id)!.name;
        }
      } catch { /* ignore localStorage errors */ }
      if (listId == null) {
        listId = active[0].id;
        listName = active[0].name;
      }
    } catch {
      notify("Couldn't load shopping lists.");
      return;
    }

    setAdding(suggestion.recipe.id);
    try {
      const results = await Promise.allSettled(
        missing.map((text) => api.shoppingAddItem(listId!, text)),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      try { localStorage.setItem(LAST_LIST_KEY, String(listId)); } catch { /* ignore */ }
      notify(`Added ${ok} item${ok === 1 ? "" : "s"} to ${listName}`);
    } catch {
      notify("Couldn't add items to list.");
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="recipe-suggestions">
      <div className="recipe-suggestions-header">Suggested for you</div>
      <div className="recipes-list">
        {suggestions.map((s) => {
          const { recipe, topExpiringItems, matched, missing } = s;
          const total = matched.length + missing.length;
          const top = topExpiringItems[0];
          const isAdding = adding === recipe.id;

          return (
            <div
              key={recipe.id}
              className="recipe-list-row"
              onClick={() => onOpenRecipe(String(recipe.id))}
              style={{ cursor: "pointer" }}
            >
              <div
                className="recipe-list-thumb"
                style={recipe.imageUrl ? undefined : { background: hueGrad(hueFromId(String(recipe.id))) }}
              >
                {recipe.imageUrl && <img src={recipe.imageUrl} alt={recipe.name} />}
              </div>
              <div className="recipe-list-meta">
                <div className="recipe-list-title">{recipe.name}</div>
                <div className="recipe-list-sub">
                  {top && (
                    <span className="recipe-suggestion-reason">
                      Uses your {top.name} — {expiryPhrase(top.days)}
                    </span>
                  )}
                  <span className="recipe-suggestion-coverage">
                    {top && " · "}
                    Have {matched.length}/{total}
                    {missing.length > 0 && (
                      <>
                        {" · need "}{missing.join(", ")}
                        {" "}
                        <button
                          className="recipe-suggestion-add-btn"
                          type="button"
                          disabled={isAdding}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleAddToList(s);
                          }}
                        >
                          {isAdding ? "Adding…" : "Add to list"}
                        </button>
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
